import React, { PropsWithChildren, useEffect } from 'react'

import { useNavigation } from 'react-router-dom'

import LayoutChanger from '@components/shared/LayoutChanger'
import { useWindowSize } from '@hooks/index'
import { useSelection } from '@providers/SelectionProvider'
import { ViewType } from '@utils/global'
import { isTabletLayout } from '@utils/mobile'

import EditButton from './EditButton'
import FilterDropdown from './FilterDropdown'
import OrderDropdown from './OrderDropdown'

interface Props {
  setViewType: (type: ViewType) => void
  viewType: ViewType
  additionalActions?: React.ReactNode | React.ReactNode[]
  order?: string | null
  hideOrdering?: boolean
  filters?: string[] | null
  showFiltering?: boolean
}

const ControlArea: React.FC<PropsWithChildren<Props>> = ({
  setViewType,
  viewType,
  children,
  additionalActions,
  order,
  hideOrdering,
  showFiltering,
  filters,
}) => {
  const navigation = useNavigation()
  const { width } = useWindowSize()

  const { isEditing, selectedItems, isSorting } = useSelection()

  // If order dropdown has changed, optimistically show the new value while fetching
  if (navigation.state === 'loading' && navigation.formData) {
    order = navigation.formData.get('order')?.toString() || order
    filters = navigation.formData.get('filters')?.toString()?.split(',') || filters
  }

  const showEditingActions = isEditing || selectedItems.length > 0

  useEffect(() => {
    if (width && viewType !== ViewType.GRID && isTabletLayout(width)) {
      setViewType(ViewType.GRID)
    }
  }, [width, viewType])

  return (
    <div className="h-16 flex flex-row items-center -mr-1 flex-wrap">
      <div className="flex-1 flex flex-row items-center">
        <EditButton />

        {!showEditingActions && !isSorting && additionalActions}
        {showEditingActions && <>{children}</>}
      </div>
      {!showEditingActions && !isSorting && (
        <div className="flex flex-row-reverse md:flex-row items-center justify-end">
          {showFiltering && <FilterDropdown activeFilters={filters} />}
          {!hideOrdering && <OrderDropdown order={order} />}
          <LayoutChanger
            setViewType={setViewType}
            viewType={viewType}
            types={[ViewType.GRID, ViewType.LIST]}
            className="md:hidden lg:flex"
          />
        </div>
      )}
    </div>
  )
}

export default ControlArea
